/**
 * Match Calculator
 * Beregner match pr. dimension og genererer insights
 */

/**
 * Grupper responses efter dimension
 * @param {Object} responses - Normaliserede responses {itemId: score, ...}
 * @param {Array} items - Items med dimension info
 * @returns {Object} Grupperet efter dimension {dimension: [scores], ...}
 */
export function groupByDimension(responses, items) {
  const grouped = {}
  
  for (const [itemId, score] of Object.entries(responses)) {
    const item = items.find(i => i.id === itemId)
    
    if (!item || !item.dimension) {
      continue
    }
    
    if (!grouped[item.dimension]) {
      grouped[item.dimension] = []
    }
    
    grouped[item.dimension].push({
      itemId,
      score,
      item
    })
  }
  
  return grouped
}

/**
 * Beregn match score pr. dimension (absolute difference)
 * @param {Object} responses1 - Normaliserede responses fra person 1
 * @param {Object} responses2 - Normaliserede responses fra person 2
 * @param {Array} items - Items med dimension info
 * @param {Object} thresholds - Match thresholds (optional)
 * @returns {Object} Match scores pr. dimension {dimension: {score, level}, ...}
 */
export function calculateDimensionMatches(responses1, responses2, items, thresholds = null) {
  const grouped1 = groupByDimension(responses1, items)
  const grouped2 = groupByDimension(responses2, items)
  
  const dimensionMatches = {}
  
  // Find fælles dimensioner
  const commonDimensions = Object.keys(grouped1).filter(dim => grouped2[dim])
  
  // Default thresholds
  const defaultThresholds = {
    highMatchMaxDiff: 0.20,
    mediumMatchMaxDiff: 0.45,
    lowMatchMinDiff: 0.46
  }
  
  const thresh = thresholds || defaultThresholds
  
  for (const dimension of commonDimensions) {
    const items1 = grouped1[dimension]
    const items2 = grouped2[dimension]
    
    // Match items på itemId
    let totalDiff = 0
    let matchedCount = 0
    
    for (const item1 of items1) {
      const item2 = items2.find(i => i.itemId === item1.itemId)
      
      if (item2) {
        const diff = Math.abs(item1.score - item2.score)
        totalDiff += diff
        matchedCount++
      }
    }
    
    if (matchedCount === 0) {
      dimensionMatches[dimension] = {
        score: 0,
        level: 'no_match',
        diff: 1.0
      }
      continue
    }
    
    const avgDiff = totalDiff / matchedCount
    const matchScore = 1 - avgDiff
    
    // Determine match level based on thresholds
    let level = 'low'
    if (avgDiff <= thresh.highMatchMaxDiff) {
      level = 'high'
    } else if (avgDiff <= thresh.mediumMatchMaxDiff) {
      level = 'medium'
    }
    
    dimensionMatches[dimension] = {
      score: matchScore,
      level,
      diff: avgDiff
    }
  }
  
  return dimensionMatches
}

/**
 * Identificer dimensions med lav match
 * @param {Object} dimensionMatches - Match scores pr. dimension
 * @param {number} threshold - Threshold for "lav match" (default 0.6)
 * @returns {Array} Liste af dimensions med lav match
 */
export function identifyLowMatches(dimensionMatches, threshold = 0.6) {
  return Object.entries(dimensionMatches)
    .filter(([dimension, match]) => {
      const score = typeof match === 'object' ? match.score : match
      return score < threshold
    })
    .map(([dimension, match]) => {
      const score = typeof match === 'object' ? match.score : match
      const level = typeof match === 'object' ? match.level : (score < 0.4 ? 'very_low' : 'low')
      return {
        dimension,
        score,
        level,
        diff: typeof match === 'object' ? match.diff : (1 - score)
      }
    })
}

/**
 * Beregn overall match score med dimension weights
 * @param {Object} dimensionMatches - Match scores pr. dimension
 * @param {Object} weights - Vægte pr. dimension (optional)
 * @returns {number} Overall match score (0..1)
 */
export function calculateOverallMatch(dimensionMatches, weights = {}) {
  const dimensions = Object.keys(dimensionMatches)
  
  if (dimensions.length === 0) {
    return 0
  }
  
  // Extract scores if dimensionMatches contains objects
  const scores = {}
  Object.entries(dimensionMatches).forEach(([dim, match]) => {
    scores[dim] = typeof match === 'object' ? match.score : match
  })
  
  // Hvis ingen weights, brug lige vægt
  const hasWeights = Object.keys(weights).length > 0
  const totalWeight = hasWeights 
    ? Object.values(weights).reduce((a, b) => a + b, 0)
    : dimensions.length
  
  let weightedSum = 0
  
  for (const dimension of dimensions) {
    const score = scores[dimension]
    const weight = weights[dimension] || 1
    weightedSum += score * weight
  }
  
  return weightedSum / totalWeight
}
